/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */
import { inject, Injectable } from '@angular/core';
import { Auth, getAuth } from '@angular/fire/auth';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken, onMessage } from '@angular/fire/messaging';
import { ExceptionEntity, ExceptionService } from '@irian-ro/exception-service-module';
import {
  LoggedInUser,
  UserPushNotificationTokenDTO,
  UserPushNotificationTokenResourceService
} from '@irian-ro/auth-shared-module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EnvironmentService } from '@irian-ro/util-environment';
import { getCircularReplacer } from '@irian-ro/util-functions';

@Injectable({
  providedIn: 'root',
})
export class FirebaseCloudMessagingService {
  private auth: Auth | undefined = undefined;

  private vapidKey = inject(EnvironmentService).getEnvironmentProperty('deliwellFirebase').vapidKey;

  constructor(
    private readonly app: FirebaseApp,
    private readonly exceptionService: ExceptionService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly userPushNotificationTokenResourceService: UserPushNotificationTokenResourceService
  ) {
    this.auth = getAuth(this.app);
  }

  async requestPermissionAndSaveToken(loggedInUser: LoggedInUser) {
    const registration = await navigator.serviceWorker.register('./ngsw-worker.js');

    getToken(getMessaging(), {
      vapidKey: this.vapidKey,
      serviceWorkerRegistration: registration,
    })
      .then((currentToken) => {
        if (currentToken) {
          this.saveUserPushToken(loggedInUser.uuid, currentToken);
        }
      })
      .catch((err) => {
        this.handleErrorRetrievingToken(err);
        this.invalidate(loggedInUser.uuid);
      });
  }

  listenAndShowNotificationsForeground() {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope')?.then((registration) => {
        registration?.showNotification(payload.notification?.title, payload.notification);
      });
    });
  }

  private handleErrorRetrievingToken(err: any) {
    const exceptionEntity: ExceptionEntity = new ExceptionEntity();
    exceptionEntity.message = 'An error occurred while retrieving token.';
    exceptionEntity.stackTrace = JSON.stringify(err, getCircularReplacer());
    if (err.code != 'messaging/permission-blocked') {
      this.exceptionService.saveException(exceptionEntity).subscribe();
    }
  }

  private saveUserPushToken(userUuid: string, currentToken: string) {
    const userPushNotificationToken = new UserPushNotificationTokenDTO();
    userPushNotificationToken.token = currentToken;
    userPushNotificationToken.userUuid = userUuid;
    userPushNotificationToken.device = this.deviceDetectorService.deviceType;
    this.userPushNotificationTokenResourceService.save(userPushNotificationToken).subscribe();
  }

  private invalidate(userUuid: string) {
    this.userPushNotificationTokenResourceService
      .invalidate(userUuid, this.deviceDetectorService.deviceType)
      .subscribe();
  }
}
